const gbp = (n) => `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const COST_TYPES = { materials: 'Materials', labour: 'Labour', subcontractor: 'Subcontractor', plant: 'Plant/Hire', overheads: 'Overheads', depreciation: 'Depreciation/Repair', fuel: 'Fuel' };

// Internal job profitability report (PDF). NOT for customers.
export default function JobReport({ user, logoUrl, job, reportNumber }) {
  const biz = user?.business_name || user?.name || 'My Business';
  const accent = user?.brand_color || '#FF5A1F';
  const profitPos = (job.profit || 0) >= 0;
  const costs = job.costs || [];
  const payments = job.payments || [];
  const diary = job.diary || [];
  const byType = job.cost_by_type || {};

  return (
    <div className="print-page" style={{ background: '#fff', color: '#0F172A', fontFamily: 'Helvetica, Arial, sans-serif', padding: '24px', width: '100%', maxWidth: '780px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #0F172A', paddingBottom: '14px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {logoUrl ? <img src={logoUrl} alt="logo" style={{ maxHeight: '54px', maxWidth: '160px', objectFit: 'contain' }} /> : null}
          <div>
            <div style={{ fontWeight: 900, fontSize: '20px', textTransform: 'uppercase', letterSpacing: '-0.02em' }}>{biz}</div>
            {user?.business_phone && <div style={{ fontSize: '12px', color: '#475569' }}>{user.business_phone}</div>}
            {user?.business_email && <div style={{ fontSize: '12px', color: '#475569' }}>{user.business_email}</div>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ background: accent, color: '#fff', padding: '4px 10px', fontWeight: 900, textTransform: 'uppercase', fontSize: '13px' }}>Job Report</div>
          <div style={{ fontFamily: 'monospace', fontSize: '12px', marginTop: '6px' }}>{reportNumber}</div>
          <div style={{ fontSize: '11px', color: '#94A3B8' }}>{new Date().toLocaleDateString('en-GB')}</div>
        </div>
      </div>

      {/* Job + customer */}
      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94A3B8' }}>Job</div>
          <div style={{ fontWeight: 800, fontSize: '18px' }}>{job.name}</div>
          {job.customer_name && <div style={{ fontSize: '13px', color: '#475569' }}>{job.customer_name}</div>}
        </div>
        <div style={{ textAlign: 'right', fontSize: '12px', color: '#475569' }}>
          <div>Status: <b style={{ textTransform: 'capitalize' }}>{(job.status || '').replace('_', ' ')}</b></div>
          {job.start_date && <div>Start: {job.start_date}</div>}
          {job.target_end_date && <div>Target end: {job.target_end_date}</div>}
          <div>Progress: {job.percent_complete || 0}%</div>
        </div>
      </div>

      {/* KPIs */}
      <table style={{ width: '100%', marginTop: '18px', borderCollapse: 'collapse', fontSize: '13px' }}>
        <tbody>
          <Row k="Quoted price (incl. VAT)" v={gbp(job.quoted_price)} />
          <Row k={`VAT (${job.vat_rate || 0}%)`} v={gbp(job.vat_amount)} />
          <Row k="Net price (ex VAT)" v={gbp(job.net_price)} />
          <Row k="Total cost so far" v={gbp(job.total_cost)} />
          <Row k="Received" v={gbp(job.received)} />
          <Row k="Outstanding" v={gbp(job.outstanding)} />
        </tbody>
      </table>

      {/* Profit highlight */}
      <div style={{ marginTop: '14px', border: '2px solid #0F172A', display: 'flex' }}>
        <div style={{ flex: 1, padding: '12px 16px', borderRight: '2px solid #0F172A' }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#94A3B8' }}>Real profit (after VAT &amp; costs)</div>
          <div style={{ fontWeight: 900, fontSize: '24px', color: profitPos ? '#059669' : '#DC2626' }}>{gbp(job.profit)}</div>
        </div>
        <div style={{ flex: 1, padding: '12px 16px', borderRight: '2px solid #0F172A' }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#94A3B8' }}>Margin</div>
          <div style={{ fontWeight: 900, fontSize: '24px', color: profitPos ? '#059669' : '#DC2626' }}>{job.margin}%</div>
        </div>
        <div style={{ flex: 1, padding: '12px 16px' }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#94A3B8' }}>Vs expected</div>
          <div style={{ fontWeight: 900, fontSize: '24px', color: (job.profit_vs_expected || 0) >= 0 ? '#059669' : '#DC2626' }}>{(job.profit_vs_expected || 0) >= 0 ? '+' : ''}{gbp(job.profit_vs_expected)}</div>
        </div>
      </div>

      {/* Cost breakdown by type */}
      <Section title="Cost breakdown" accent={accent}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <tbody>
            {Object.keys(byType).length === 0 ? <tr><td style={{ padding: '6px 0', color: '#94A3B8' }}>No costs logged.</td></tr> :
              Object.entries(byType).map(([t, amt]) => <Row key={t} k={COST_TYPES[t] || t} v={gbp(amt)} />)}
          </tbody>
        </table>
      </Section>

      {/* Cost entries */}
      {costs.length > 0 && (
        <Section title="Cost entries" accent={accent}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead><tr style={{ borderBottom: '1px solid #cbd5e1', textAlign: 'left' }}><th style={{ padding: '4px 0' }}>Date</th><th>Type</th><th>Description</th><th style={{ textAlign: 'right' }}>Amount</th></tr></thead>
            <tbody>
              {costs.map((c) => (
                <tr key={c.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '4px 0' }}>{c.date || ''}</td>
                  <td>{COST_TYPES[c.type] || c.type}</td>
                  <td>{c.description || '—'}{c.supplier ? ` · ${c.supplier}` : ''}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{gbp(c.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Payments */}
      {payments.length > 0 && (
        <Section title="Payments received" accent={accent}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '4px 0' }}>{p.date || ''}</td>
                  <td>{p.description || 'Payment'}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', color: '#059669' }}>{gbp(p.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Diary */}
      {diary.length > 0 && (
        <Section title="Site diary" accent={accent}>
          {diary.map((d) => (
            <div key={d.id} style={{ borderLeft: `2px solid ${accent}`, paddingLeft: '10px', marginBottom: '6px', fontSize: '12px' }}>
              <div>{d.note}</div>
              <div style={{ fontFamily: 'monospace', fontSize: '10px', color: '#94A3B8' }}>{d.ts ? new Date(d.ts).toLocaleString('en-GB') : ''}</div>
            </div>
          ))}
        </Section>
      )}

      <div style={{ marginTop: '20px', paddingTop: '10px', borderTop: '2px solid #0F172A', fontSize: '10px', color: '#94A3B8', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.16em', fontFamily: 'monospace' }}>
        Internal job report — not for customer distribution · tradesmateuk.co.uk
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
      <td style={{ padding: '6px 0', color: '#475569' }}>{k}</td>
      <td style={{ padding: '6px 0', textAlign: 'right', fontFamily: 'monospace', fontWeight: 700 }}>{v}</td>
    </tr>
  );
}

function Section({ title, accent = '#FF5A1F', children }) {
  return (
    <div style={{ marginTop: '18px' }}>
      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.14em', color: accent, fontWeight: 700, marginBottom: '6px' }}>{title}</div>
      {children}
    </div>
  );
}
